import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Popconfirm, Select, Space, Tabs } from "antd";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import "../assets/scss/projects.scss";
import AuditLogButton from "../components/AuditLogButton";
import CustomForm from "../components/CustomForm";
import FormButtonCancel from "../components/FormButtonCancel";
import FormButtonSave from "../components/FormButtonSave";
import LoadingComponent from "../components/LoadingComponent";
import AuditLogModal from "../components/Modal/AuditLogModal";
import useFormHandler from "../hooks/useFormHandler";
import useTabKey from "../hooks/useTabKey";
import {
	deleteMultiAssetOperationTemplate,
	deleteMultiAssetProjectTemplate,
	getMultiAssetProgrammeTemplate,
	updateMultiAssetOperationTemplate,
	updateMultiAssetProjectTemplate,
} from "../services/api-server/multi_asset_programme_template";
import { getOperations } from "../services/api-server/operations";
import {
	deleteWellProgrammeTemplate,
	getWellProgrammeTemplateList,
	updateWellProgrammeTemplate,
} from "../services/api-server/well_programme_template";
import { alertMessage } from "../utils/alert";
import Emitter from "../utils/emitter";
import { HomeContext } from "./Home";
import ProgrammeSettingsWorkflowConfig from "./ProgrammeSettingsWorkflowConfig";

const tabList: any = [
	{ label: "Programme details", key: "well_programme_template" },
	{ label: "Workflow configuration", key: "well-programme" },
];

function WellProgrammeTemplate(props: any) {
	const location = useLocation();
	const navigate = useNavigate();

	const { tabKey, handleTabChange } = useTabKey("well_programme_template");
	const { formRef, formReset, setFormReset, resetForm, initialForm } =
		useFormHandler();

	const [templateList, setTemplateList] = useState<any>([]);
	const [isLoading, setIsLoading] = useState<any>(true);
	const [currentTemplateData, setCurrentTemplateData] = useState<any>(null);
	const [customFormTabKey, setCustomFormTabKey] = useState<any>(null);
	const [customFormOption, setCustomFormOption] = useState<any>(null);
	const [parentTemplateData, setParentTemplateData] = useState<any[]>([]);
	const [auditLogModalOpen, setAuditLogModalOpen] = useState<any>(false);

	const {
		currentTenantKey,
		handleFormUpdating,
		userInfo,
		handlePageChange,
	}: any = useContext(HomeContext);

	useEffect(() => {
		const findTabKey = () => {
			if (sessionStorage.getItem("main-tabkey")) {
				let sessionTabKey: any = sessionStorage.getItem("main-tabkey");
				let found = tabList.find((tab: any) => {
					return tab.key === sessionTabKey;
				});
				if (found) {
					return sessionTabKey;
				} else {
					sessionStorage.setItem("main-tabkey", "well_programme_template");
					return "well_programme_template";
				}
			} else {
				sessionStorage.setItem("main-tabkey", "well_programme_template");
				return "well_programme_template";
			}
		};
		const key = findTabKey();
		handleTabChange(key);
	}, []);

	const fetchData = async (tenantKey: any) => {
		setIsLoading(true);
		if (location.state?.programme_type?.toLowerCase() === "operation") {
			const parent_id = location.state?.parent_campaign;
			getMultiAssetProgrammeTemplate(tenantKey, { _id: parent_id })
				.then((values: any) => {
					const operationsTemplateData = values[0]?.operations?.map(
						(element: any) => {
							// Get tasks from phases
							const phaseTasks =
								values[0]?.phases
									?.map((element: any) => [...(element?.tasks ?? [])])
									.flat(Infinity) || [];

							// Get tasks from operations
							const operationTasks =
								values[0]?.operations
									?.map((element: any) => [...(element?.tasks ?? [])])
									.flat(Infinity) || [];

							// Combine all tasks from both phases and operations
							const allCampaignTasks = [...phaseTasks, ...operationTasks];

							return {
								...element,
								name: element?.operation_name,
								parent_campaign: values[0]?._id,
								allCampaignTasks: allCampaignTasks,
							};
						}
					);
					setParentTemplateData(values);
					const templateList = [...operationsTemplateData];
					console.log("templateList from operations", templateList);
					setTemplateList(templateList);
					setIsLoading(false);
				})
				.catch((e: any) => {
					console.log(e);
					setIsLoading(false);
				});
		} else {
			Promise.all([
				getWellProgrammeTemplateList(tenantKey),
				getMultiAssetProgrammeTemplate(tenantKey),
				getOperations(tenantKey, { project_template: { $ne: null } }),
			])
				.then((values: any) => {
					const wellProgrammeTemplateData = values[0].map((item: any) => {
						return {
							...item,
							useCount:
								values[2]?.filter(
									(operation: any) => operation.project_template === item._id
								).length || "0",
						};
					});
					const templateList = [...wellProgrammeTemplateData, ...values[1]];
					setTemplateList(templateList);
					setIsLoading(false);
				})
				.catch((e: any) => {
					console.log(e);
					setIsLoading(false);
				});
		}
		return true;
	};

	useEffect(() => {
		if (currentTenantKey) {
			fetchData(currentTenantKey)
				.then(() => {
					console.log("This is template page");
				})
				.catch((e: any) => {
					console.log(e);
					setIsLoading(false);
				});
		}
	}, [currentTenantKey, location.state]);

	useEffect(() => {
		if (location.state?.programme_type) {
			setCustomFormTabKey("multi_asset_programme_template");
			if (location.state?.programme_type?.toLowerCase() === "operation") {
				setCustomFormOption("general_multi_asset_template_operation");
			} else {
				setCustomFormOption("general_multi_asset_template_project");
			}
		} else {
			setCustomFormTabKey("well_programme_template");
			setCustomFormOption("general");
		}
	}, [location.state, tabKey]);

	useEffect(() => {
		if (location.state) {
			console.log("Triggered");
			let foundTemplate = templateList.find(
				(element: any) => element._id == location.state._id
			);
			setCurrentTemplateData(foundTemplate);
			resetForm(foundTemplate);
		} else {
			setCurrentTemplateData(templateList[0]);
			resetForm(templateList[0]);
		}
	}, [templateList, tabKey, location.state]);

	useEffect(() => {
		Emitter.on("updateTemplate", (updatedItem: any) => {
			fetchData(currentTenantKey);
			// setTemplateList((prevList: any) =>
			// 	prevList.map((item: any) => {
			// 		if (item._id === updatedItem._id) {
			// 			return {
			// 				...item,
			// 				...Object.fromEntries(
			// 					Object.entries(updatedItem).filter(
			// 						([key, value]) => value !== undefined && value !== item[key]
			// 					)
			// 				),
			// 			};
			// 		}
			// 		return item;
			// 	})
			// );
		});

		return () => {
			Emitter.remove("updateTemplate");
		};
	}, [templateList]);

	const handleFormSave = (values: any | null = null) => {
		console.log("values", values);
		const formList =
			location.state?.programme_type?.toLowerCase() === "operation"
				? ["operation_name", "description"]
				: ["name", "description"];
		if (location.state?.programme_type) {
			if (location.state?.programme_type?.toLowerCase() === "operation") {
				updateMultiAssetOperationTemplate(
					currentTenantKey,
					location.state?.parent_campaign,
					{
						_id: currentTemplateData._id,
						...values,
					},
					initialForm,
					userInfo.user,
					formList
				)
					.then((_: any) => {
						alertMessage(
							"success",
							"Operation project template has been successfully updated."
						);
						fetchData(currentTenantKey);
						handleFormUpdating(false);
					})
					.catch((_: any) => {
						alertMessage("error", "Failed to update. Please try again.");
						resetForm(currentTemplateData);
						setIsLoading(false);
					});
			} else {
				updateMultiAssetProjectTemplate(
					currentTenantKey,
					currentTemplateData._id,
					values,
					initialForm,
					userInfo.user,
					formList
				)
					.then((_: any) => {
						alertMessage(
							"success",
							"Project template has been successfully updated."
						);
						fetchData(currentTenantKey);
						handleFormUpdating(false);
					})
					.catch((_: any) => {
						alertMessage("error", "Failed to update. Please try again.");
						resetForm(currentTemplateData);
						setIsLoading(false);
					});
			}
		} else {
			updateWellProgrammeTemplate(
				currentTenantKey,
				currentTemplateData._id,
				values,
				initialForm,
				userInfo.user,
				formList
			)
				.then((_: any) => {
					alertMessage(
						"success",
						"Programme details has been successfully updated."
					);
					fetchData(currentTenantKey);
					handleFormUpdating(false);
				})
				.catch((_: any) => {
					alertMessage("error", "Failed to update. Please try again.");
					resetForm(currentTemplateData);
					setIsLoading(false);
				});
		}
	};

	const handleFormCancel = () => {
		handleFormUpdating(false);
		resetForm(currentTemplateData);
	};

	const handleFormDelete = (id: any) => {
		setIsLoading(true);
		handleFormUpdating(false);
		// programme type is unique for multi asset programme template
		if (location.state?.programme_type) {
			if (location.state?.programme_type?.toLowerCase() === "operation") {
				const parent_id = formRef.getFieldValue("parent_campaign");
				deleteMultiAssetOperationTemplate(
					currentTenantKey,
					parent_id,
					id,
					userInfo.user
				)
					.then((_: any) => {
						alertMessage(
							"success",
							"Operation project template has been successfully deleted."
						);
						resetForm();
						setIsLoading(false);
						navigate("/administration", { replace: true });
					})
					.catch(() => {
						alertMessage(
							"error",
							"Failed to delete operation project template. Please try again."
						);
						setIsLoading(false);
					});
			} else {
				deleteMultiAssetProjectTemplate(currentTenantKey, id, userInfo.user)
					.then((_: any) => {
						alertMessage(
							"success",
							"Project template has been successfully deleted."
						);
						resetForm();
						setIsLoading(false);
						navigate("/administration", { replace: true });
					})
					.catch(() => {
						alertMessage(
							"error",
							"Failed to delete project template. Please try again."
						);
						setIsLoading(false);
					});
			}
		} else {
			deleteWellProgrammeTemplate(currentTenantKey, id, userInfo.user)
				.then((_: any) => {
					alertMessage(
						"success",
						"Programme details has been successfully deleted."
					);
					resetForm();
					setIsLoading(false);
					navigate("/administration", { replace: true });
				})
				.catch(() => {
					alertMessage(
						"error",
						"Failed to delete programme details. Please try again."
					);
					setIsLoading(false);
				});
		}
	};

	const handleTemplateChange = async (key: any = null) => {
		try {
			await handlePageChange();
			handleTabChange("well_programme_template");
			sessionStorage.removeItem("tabKey");
			sessionStorage.removeItem("tabKey");
			const templateRecord = templateList.find((template: any) => {
				return template._id == key;
			});
			setCurrentTemplateData(templateRecord);
			resetForm(templateRecord);
			navigate(`/administration/programme-settings`, {
				state: templateRecord,
			});
		} catch {}
	};

	const tabContent = () => {
		switch (tabKey) {
			case "well_programme_template":
				return (
					<div
						style={{
							flex: "1",
							display: "flex",
							flexDirection: "row",
							overflow: "auto",
						}}
					>
						<div
							className="main-container"
							style={{
								width: "70%",
								display: "flex",
								flexDirection: "column",
								borderRadius: "3px",
							}}
						>
							<div className="generic-header" style={{ height: "50px" }}>
								Settings
							</div>
							<div className="generic-content" style={{ flex: "1" }}>
								{customFormTabKey && (
									<CustomForm
										formRef={formRef}
										setFormReset={setFormReset}
										handleFormSave={handleFormSave}
										tabKey={customFormTabKey}
										formOption={customFormOption}
										activeRecord={currentTemplateData}
										dataOptions={{
											campaign: parentTemplateData,
										}}
									/>
								)}
							</div>
							<div className="generic-footer" style={{ height: "50px" }}>
								<Popconfirm
									overlayInnerStyle={{
										maxWidth: "300px",
										padding: "16px",
									}}
									placement="topLeft"
									okText="Delete"
									okType="danger"
									title=""
									icon={<ExclamationCircleOutlined style={{ color: "grey" }} />}
									cancelText={"Cancel"}
									cancelButtonProps={{ ghost: true }}
									overlayClassName="popconfirm-danger"
									description="Permanently delete this well programme? This action cannot be undone."
									// onOpenChange={(open: any) => {
									// 	if (!open) {
									// 		setEmptyModalOpen(false);
									// 	}
									// }}
									onConfirm={() => {
										// setEmptyModalOpen(false);
										handleFormDelete(currentTemplateData?._id);
									}}
									// onCancel={() => {
									// 	setEmptyModalOpen(false);
									// }}
								>
									<Button
										className="ant-btn-secondary"
										style={{ marginRight: "auto" }}
										danger
									>
										Delete
									</Button>
								</Popconfirm>

								<AuditLogButton
									moduleKey="project-delivered"
									handleClick={() => {
										setAuditLogModalOpen(true);
									}}
								/>
								<FormButtonCancel
									form={formRef}
									handleFormCancel={handleFormCancel}
									formReset={formReset}
								/>
								<FormButtonSave
									form={formRef}
									handleFormSave={handleFormSave}
									formReset={formReset}
									setFormReset={setFormReset}
								/>
							</div>
						</div>
					</div>
				);
			case "well-programme":
				return (
					<ProgrammeSettingsWorkflowConfig
						currentTemplateData={currentTemplateData}
						tabKey={customFormTabKey}
						formOption={customFormOption}
					/>
				);
		}
	};

	return (
		<>
			{/* <LoadingComponent isLoading={isLoading}> */}
			<div className="generic-container">
				<Space direction="vertical">
					<Select
						getPopupContainer={(trigger: any) => trigger.parentNode}
						className="title-selector"
						style={{
							fontSize: "20px",
							fontWeight: "600",
							color: "rgba(255, 255, 255, 0.85)",
							width: "auto",
						}}
						options={templateList}
						value={currentTemplateData?._id}
						// open={true}
						// onClick={() => {
						// 	setDropdownOpen(!dropdownOpen);
						// }}
						onChange={(key: any) => {
							console.log(key);
							handleTemplateChange(key);
							// setDropdownOpen(false);
						}}
						onBlur={(event: any) => {
							event.preventDefault();
							// setDropdownOpen(false);
						}}
						fieldNames={{
							label: "name",
							value: "_id",
						}}
						dropdownStyle={{ minWidth: "100%", width: "auto" }}
						dropdownRender={(menu) => {
							return <>{menu}</>;
						}}
					/>
					<Tabs
						activeKey={tabKey}
						onChange={(key: any) => {
							handleTabChange(key);
							setFormReset(true);
							resetForm([]);
						}}
						items={tabList}
					/>
				</Space>
				<div
					style={{
						flex: "1",
						display: "flex",
						flexDirection: "row",
						gap: "20px",
						maxHeight: "100%",
						overflow: "auto",
					}}
				>
					{tabContent()}
				</div>
			</div>
			{/* </LoadingComponent> */}
			{auditLogModalOpen && (
				<AuditLogModal
					// {...auditLogProps}
					tenantKey={currentTenantKey}
					open={auditLogModalOpen}
					handleClose={() => setAuditLogModalOpen(false)}
					label={
						customFormTabKey === "well_programme_template"
							? "Well programme template"
							: "Multi-asset programme template"
					}
					type={
						customFormTabKey === "well_programme_template"
							? "well_programme_template"
							: "multi_asset_programme_template"
					}
					identifierKeys={{}}
					auditlogFilter={[
						{
							level:
								customFormTabKey === "well_programme_template"
									? "well_programme_template"
									: {
											$in: [
												"operation_multi_asset_template",
												"multi_asset_programme_template",
											],
										},
							level_id: currentTemplateData?._id,
							event_type: { $in: ["add", "update", "delete"] },
						},
					]}
				/>
			)}
		</>
	);
}

export default WellProgrammeTemplate;
